import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab
} from '@mui/material'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'

import { useGetWorkSpaceUserMutation } from 'src/services/user-service'
import {
  useDeleteMemberMutation,
  useGetMembersMutation,
  useSendInvitationMutation
} from 'src/services/white-label-team-management-service'
import toast from 'react-hot-toast'

import { useSelector } from 'react-redux'
import HeaderWithPopover from 'src/components/HeaderWithPopover'
import TeamMembersTable from './TeamMembersTable'
import PendingInvitationsTable from './PendingInvitationsTable'
import PermissionDialog from './PermissionDialog'
function CustomTabPanel (props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{}}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
}

const WhiteLabelTeamManagementPage = ({ tab }) => {
  const workspace = useSelector((state) => state.workspace)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [getWorkSpaceUser] = useGetWorkSpaceUserMutation()
  const [getMembers] = useGetMembersMutation()
  const [sendInvitation] = useSendInvitationMutation()
  const [deleteMember] = useDeleteMemberMutation()
  const [user, setUser] = useState({})
  const [pendingMembers, setPendingMembers] = useState([])
  const [teamMembers, setTeamMembers] = useState([])
  const theme = useTheme()

  const [value, setValue] = useState(
    tab === 'Team Members' ? 0 : tab === 'Pending Invitations' ? 1 : 0
  )

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  useEffect(() => {
    setValue(tab === 'Team Members' ? 0 : tab === 'Pending Invitations' ? 1 : 0)
  }, [tab])

  const openDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseInviteDialog = (closeWithSave) => {
    if (closeWithSave) {
      fetchPendingMembers()
    }
    setDialogOpen(false)
  }

  const getUser = async () => {
    const user = await getWorkSpaceUser(workspace?.createdBy).unwrap()
    setUser(user)
  }

  useEffect(() => {
    if (workspace?._id) {
      getUser()
      fetchTeamMembers()
      fetchPendingMembers()
    }
  }, [workspace])

  const fetchTeamMembers = async () => {
    try {
      const { data } = await getMembers({ status: 'accepted' })
      setTeamMembers(data?.data?.docs)
    } catch (error) {
      toast.error(error?.data?.error?.message)
    }
  }

  const fetchPendingMembers = async () => {
    try {
      const { data } = await getMembers({ status: 'pending' })
      setPendingMembers(data?.data?.docs || [])
    } catch (error) {
      toast.error(error?.data?.error?.message)
    }
  }

  const handleRemoveMemberClick = async (member,callback) => {
    if (member) {
      const {data,error} = await deleteMember(member)
      callback(data,error)
    }
  }

  const handleResendInvitation = async (member) => {
    try {
      const requestBody = {
        email: member.email,
        permissions: {
          manageSubUsers: member.permissions.manageSubUsers,
          manageBranding: member.permissions.manageBranding,
          managePlans: member.permissions.managePlans,
          manageTeamMembers: member.permissions.manageTeamMembers
        }
      }

      const { error } = await sendInvitation(requestBody)
      if (error) {
        toast.error(error.data.error.message)
      }
    } catch (error) {
      toast.error(error.message ?? 'Failed to send the invitation. Please try again.')
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: '90%' },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}
        >
          <HeaderWithPopover
            title='Team Members'
          />
        </Box>
      </Box>
      <Box
        sx={{
          margin: 2,
          padding: { xs: 2, sm: 4 },
          backgroundColor: '#fff',
          minHeight: '70vh',
          border: '1px solid #D7D9E6',
          borderRadius: '8px'
        }}
      >
        <Box>
          <Typography variant='h6' fontWeight='bold' color={theme.palette.primary.primaryText}>
            Members
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: '13px', color: '#8181B0' }}>
            Manage all Team members and pending invitations.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            marginBottom: '16px',
            mt: 2,
            gap: { xs: 2, sm: 0 },
            flexWrap: 'wrap'
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            sx={{
              marginBottom: '16px',
              background: '#E1E3EC',
              border: '1ps solid #D7D9E6',
              borderRadius: '8px',
              width: { xs: '100%', sm: 'auto' },
              '.MuiTabs-flexContainer': {
                borderBottom: '1px solid #ddd',
                flexDirection: { xs: 'row', sm: 'row' }
              },
              '.MuiTab-root': {
                textTransform: 'none',
                minWidth: { xs: '5%', sm: '400px' },
                color: 'black'
              },
              '& .Mui-selected': {
                backgroundColor: '#ffffff',
                color: `${theme.palette.primary.main}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab
              sx={{
                my: { xs: '4px', sm: '5px' },
                mx: { xs: '4px', sm: '5px' },
                px: { xs: 3, sm: 6 },
                py: { xs: '8px', sm: '4px' }
              }}
              label='Team Members'
            />
            <Tab
              sx={{
                my: { xs: '4px', sm: '5px' },
                mx: { xs: '4px', sm: '5px' },
                px: { xs: 3, sm: 6 },
                py: { xs: '8px', sm: '4px' }
              }}
              label='Pending Invitations'
            />
          </Tabs>
          <Box sx={{}}>
            <Button
              variant='contained'
              onClick={openDialog}
              sx={{
                bgcolor: `${theme.palette.primary.main}`,
                color: `${theme.palette.primary.contrastText}`,
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}`
                },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '120px' },
                whiteSpace: 'nowrap',
                fontSize: { xs: '14px', sm: '16px' },
                padding: { xs: '8px 16px', sm: '8px 24px' }
              }}
            >
              Invite Member
            </Button>
          </Box>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <TeamMembersTable fetchTeamMembers={fetchTeamMembers} teamMembers={teamMembers} handleRemoveMemberClick={handleRemoveMemberClick} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <PendingInvitationsTable pendingMembers={pendingMembers} handleRemoveMemberClick={handleRemoveMemberClick} handleResendInvitation={handleResendInvitation} />
        </CustomTabPanel>
      </Box>

      <PermissionDialog
        openManagePermissionsDialog={dialogOpen}
        handleCloseManagePermissionsDialog={handleCloseInviteDialog}
        user={user}
      />

    </>
  )
}

export default WhiteLabelTeamManagementPage
